const SUPPORTED_LANGS = ["en", "fr", "es", "de", "it"] as const;
const SUPPORTED_TOPICS = [
  "game-night",
  "family-night",
  "date-night",
  "birthday-party",
  "office-party",
] as const;

export type CustomLandingLang = (typeof SUPPORTED_LANGS)[number];
export type CustomLandingTopic = (typeof SUPPORTED_TOPICS)[number];

export type CustomLandingContext = {
  lang: CustomLandingLang;
  topic: CustomLandingTopic | "";
  source: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  hadLangFallback: boolean;
  hadTopicFallback: boolean;
};

const SUPPORTED_LANG_SET = new Set<string>(SUPPORTED_LANGS);
const SUPPORTED_TOPIC_SET = new Set<string>(SUPPORTED_TOPICS);

const TOPIC_LABELS: Record<
  CustomLandingTopic,
  Record<CustomLandingLang, string>
> = {
  "game-night": {
    en: "game night",
    fr: "soiree jeux",
    es: "noche de juego",
    de: "Spieleabend",
    it: "serata giochi",
  },
  "family-night": {
    en: "family night",
    fr: "soiree en famille",
    es: "noche en familia",
    de: "Familienabend",
    it: "serata in famiglia",
  },
  "date-night": {
    en: "date night",
    fr: "soiree en amoureux",
    es: "noche de cita",
    de: "Date-Abend",
    it: "serata romantica",
  },
  "birthday-party": {
    en: "birthday party",
    fr: "fete d'anniversaire",
    es: "fiesta de cumpleanos",
    de: "Geburtstagsparty",
    it: "festa di compleanno",
  },
  "office-party": {
    en: "office party",
    fr: "fete de bureau",
    es: "fiesta de oficina",
    de: "Burofeier",
    it: "festa in ufficio",
  },
};

const normalizeParamValue = (value: unknown, maxLength = 120) =>
  String(value || "").trim().slice(0, maxLength);

const normalizeLang = (value: unknown): CustomLandingLang => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .split("-")[0];

  return SUPPORTED_LANG_SET.has(normalized)
    ? (normalized as CustomLandingLang)
    : "en";
};

const normalizeTopic = (value: unknown): CustomLandingTopic | "" => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

  return SUPPORTED_TOPIC_SET.has(normalized)
    ? (normalized as CustomLandingTopic)
    : "";
};

const readParam = (
  source:
    | URLSearchParams
    | Record<string, unknown>
    | undefined,
  key: string
) => {
  if (!source) return "";
  if (source instanceof URLSearchParams) {
    return source.get(key) || "";
  }
  return String(source[key] || "");
};

export const normalizeCustomLandingContext = (
  source?:
    | URLSearchParams
    | Record<string, unknown>
) => {
  const rawLang = readParam(source, "lang") || readParam(source, "language");
  const rawTopic = readParam(source, "topic");
  const normalizedRawLang = String(rawLang || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .split("-")[0];

  const lang = normalizeLang(rawLang);
  const topic = normalizeTopic(rawTopic);

  return {
    lang,
    topic,
    source: normalizeParamValue(readParam(source, "source")),
    utmSource: normalizeParamValue(readParam(source, "utm_source")),
    utmMedium: normalizeParamValue(readParam(source, "utm_medium")),
    utmCampaign: normalizeParamValue(readParam(source, "utm_campaign")),
    hadLangFallback: Boolean(rawLang) && lang !== normalizedRawLang,
    hadTopicFallback: Boolean(rawTopic) && !topic,
  } satisfies CustomLandingContext;
};

export const hasCustomLandingContext = (context: CustomLandingContext) =>
  Boolean(
    context.lang !== "en" ||
      context.topic ||
      context.source ||
      context.utmSource ||
      context.utmMedium ||
      context.utmCampaign
  );

export const buildCustomLandingSearch = (
  context: Partial<CustomLandingContext>
) => {
  const params = new URLSearchParams();

  if (context.lang) params.set("lang", context.lang);
  if (context.topic) params.set("topic", context.topic);
  if (context.source) params.set("source", context.source);
  if (context.utmSource) params.set("utm_source", context.utmSource);
  if (context.utmMedium) params.set("utm_medium", context.utmMedium);
  if (context.utmCampaign) params.set("utm_campaign", context.utmCampaign);

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const buildCustomLandingPath = (
  pathname: string,
  context: Partial<CustomLandingContext>
) => `${pathname}${buildCustomLandingSearch(context)}`;

export const getCustomLandingTopicLabel = (
  topic: CustomLandingTopic | "",
  lang: CustomLandingLang
) => {
  if (!topic) return "";
  return TOPIC_LABELS[topic][lang] || TOPIC_LABELS[topic].en;
};

export const extractCustomLandingContextFromDraft = (
  draft: Record<string, unknown> | null | undefined
) =>
  normalizeCustomLandingContext({
    lang: draft?.language,
    topic: draft?.topic,
    source: draft?.attributionSource,
    utm_source: draft?.utmSource,
    utm_medium: draft?.utmMedium,
    utm_campaign: draft?.utmCampaign,
  });
